import { useState, useCallback } from 'react';
import { MediaItem } from './MediaItem.model';
import { useRole } from '@/contexts/RoleContext';

export type DownloadStatus = 'checking' | 'downloading' | 'ready' | 'failed' | null;

interface UseMediaDownloadOptions {
  autoHideDelay?: number; // ms to auto-hide status after ready/failed
}

interface UseMediaDownloadReturn {
  downloadStatus: DownloadStatus;
  isDownloading: boolean;
  download: (mediaItem: MediaItem) => Promise<void>;
}

/**
 * Composable hook for media item downloads
 * Handles download state, progress tracking, and auto-hide
 * Automatically uses the current role from RoleContext
 */
export const useMediaDownload = (
  options: UseMediaDownloadOptions = {}
): UseMediaDownloadReturn => {
  const { autoHideDelay = 3000 } = options;
  const { role } = useRole();
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(null);

  const download = useCallback(
    async (mediaItem: MediaItem) => {
      try {
        await mediaItem.downloadFile(role, (status) => {
          setDownloadStatus(status);

          // Auto-hide status after completion
          if (status === 'ready' || status === 'failed') {
            setTimeout(() => setDownloadStatus(null), autoHideDelay);
          }
        });
      } catch (err) {
        console.error('❌ Failed to download:', err);
        setDownloadStatus('failed');
        setTimeout(() => setDownloadStatus(null), autoHideDelay);
      }
    },
    [role, autoHideDelay]
  );

  const isDownloading = downloadStatus === 'checking' || downloadStatus === 'downloading';

  return {
    downloadStatus,
    isDownloading,
    download,
  };
};
